// brain.ts — the AI client. The ONE billed AI call this app makes:
//   POST https://api.orioralabs.com/api/apps/oriora_knowcode/ask
// with the customer's Oriora session token. The server holds the prompt + bills the user's Oriora
// wallet. THIS IS A BYOK-ONLY APP — the app registers with default_byok=true and the customer must
// connect their own vendor key in Oriora Settings (Managed BYOK lane). Whole repositories stay local;
// ONLY the matched code snippets (retrieved on-device by Rust FTS5) + the question are sent per call.
//
// CRUD, not agentic: text-in (scoped JSON of code snippets + question), text-out (answer + citations).
// No agent loop, no second call, no tool-use chain, no direct OpenAI/Anthropic SDK in the app.
//
// The answer is GROUNDED + CITED: it explains the code using only the snippets we sent and cites each
// claim with a file:line. The deterministic symbol map (parsed on-device) answers "where is X defined?"
// without a call at all. The server prompt enforces the grounded-only + cite-everything contract.
//
// Also exports `openWebLogin` — the desktop in-app login window trigger (native auth plumbing).

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { Snippet } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_knowcode";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * The single billed-AI transport. POSTs `body` to /api/apps/oriora_knowcode/<endpoint> with the
 * customer's Oriora Bearer token. Retries transient failures (502/503/504 + network); hard errors
 * (401/402/403) never retry and surface as typed BrainError codes.
 */
// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) {
        await sleep(attempt * 800);
        continue;
      }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402)
      throw new BrainError("insufficient_funds", "Add funds in your Oriora account to keep using AI.");
    if (res.status === 403)
      throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) {
        await sleep(attempt * 800);
        continue;
      }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

/** One citation the server returns. `path`/`start_line`/`end_line` are copied from a snippet we sent. */
export interface AskCitation {
  path: string;
  start_line: number;
  end_line: number;
  /** Short note on what that code contributed to the answer. */
  why: string;
}

export interface AskResult {
  answer: string;
  citations: AskCitation[];
  /** 0..1 — the model's grounding confidence (low ⇒ "not clearly in the indexed code"). */
  confidence: number;
}

/** A code snippet as sent to the brain — the ONLY repo content that leaves the device. */
export interface SnippetRef {
  path: string;
  lang: string;
  start_line: number;
  end_line: number;
  code: string;
}

/**
 * Ask the indexed codebase a plain-English question. The ONE AI feature.
 *
 * Flow: the caller has already run the LOCAL retrieval (store.searchCode) to get the top matching
 * snippets ON-DEVICE. Here we send ONLY those snippets + the question. The server answers using ONLY
 * those snippets and cites file + line range. The whole repository never leaves the device.
 *
 * The endpoint + Bearer JWT: POST /api/apps/oriora_knowcode/ask. BYOK-only — the customer's own vendor
 * key (connected in Oriora Settings) runs the call; a 403 means no key is connected yet.
 */
export async function askCodebase(question: string, snippets: Snippet[]): Promise<AskResult> {
  if (snippets.length === 0) {
    // No local match — answer honestly without spending a call.
    return {
      answer:
        "I couldn't find that in the indexed code. Try different words (a function or file name helps), or re-index the folder if it's changed.",
      citations: [],
      confidence: 0,
    };
  }
  const refs: SnippetRef[] = snippets.map((s) => ({
    path: s.path,
    lang: s.lang,
    start_line: s.start_line,
    end_line: s.end_line,
    code: s.code,
  }));
  // Citation guard prep: the set of (path) we actually sent — drop any cite the model invents.
  const sentPaths = new Set(snippets.map((s) => s.path));

  const r = await brainCall<Partial<AskResult>>("ask", {
    question,
    snippets: refs,
    now_ts: Math.floor(Date.now() / 1000),
  });

  const citations: AskCitation[] = Array.isArray(r.citations)
    ? r.citations
        .filter((c): c is AskCitation => !!c && typeof c.path === "string" && sentPaths.has(c.path))
        .slice(0, 10)
    : [];

  return {
    answer: String(r.answer ?? "").trim(),
    citations,
    confidence: typeof r.confidence === "number" ? r.confidence : 0,
  };
}

/**
 * "Snap code" — read the code / error message / diagram text out of a PHOTO, through the ORIORA
 * BRAIN. The router picks an image-capable model. Returns the extracted plain text (no formatting),
 * which App.tsx feeds straight into the on-device codebase search (store.searchCode) so the user can
 * find what a colleague's screen, a book or a slide is showing inside their own indexed repo.
 *
 * This is an ordinary billed brain call, exactly like askCodebase — BYOK-only, the customer's own
 * vendor key. The downscaled JPEG (photo.ts) is the only thing that leaves the device on this path,
 * and only for this one call; Oriora retains nothing. A 403 means no vendor key is connected yet.
 * Server endpoint: POST /api/apps/oriora_knowcode/extract-code (see EXTRACT_SYSTEM_PROMPT below).
 */
export async function extractCodeFromPhoto(photoDataUrl: string): Promise<string> {
  const url = (photoDataUrl ?? "").trim();
  if (!url.startsWith("data:image/")) throw new BrainError("unavailable", "Attach a photo first.");
  const r = await brainCall<{ text?: unknown }>("extract-code", { image: url });
  // The model returns "" when it finds nothing readable — the honest empty answer. Anything
  // unparseable narrows to "" too, so the caller shows the "couldn't read it" state, never a crash.
  return typeof r.text === "string" ? r.text.trim() : "";
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_knowcode/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/oriora_knowcode/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS browser —
// the in-app https handback is robust on iOS where the custom-scheme deep-link is fragile.
// Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must allowlist
// `oriora-knowcode` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");

// ─────────────────────────────────────────────────────────────────────────────
// SERVER PROMPT (operator must mirror this at oriora-server for /api/apps/oriora_knowcode/ask).
// Kept here as the source-of-truth contract. The server runs ONE grounded call over the snippets.
// (The server route itself is the NEXT stage — not written here.)
// ─────────────────────────────────────────────────────────────────────────────
export const ASK_SYSTEM_PROMPT = `You are Oriora KnowCode's code assistant. You answer the user's question USING ONLY the code snippets provided in this request. Each snippet carries its file path, language, and an exact line range (e.g. "src/auth/login.ts:40-58").

ABSOLUTE RULES:
1. ANSWER ONLY FROM THE SNIPPETS. Never use outside knowledge about the codebase, and never assume code that isn't shown. If the snippets don't contain the answer, say plainly: "I couldn't find that in the indexed code." Do not guess.
2. EXPLAIN + LOCATE. Describe what the code does and where it lives, in plain language. When the user asks "where is X handled/defined", point to the exact file and line range.
3. ALWAYS CITE. For every claim, reference the file path and line range it came from. Only cite files that appear in the provided snippets; never invent a file, path, line number or function.
4. BE EXACT. Quote identifiers, function names and signatures verbatim from the snippets; do not rename or paraphrase them. Do not describe behaviour the snippets don't show.
5. BE BRIEF AND PLAIN. Two to five sentences. Lead with the direct answer. No preamble, no restating the question.

Return JSON: { "answer": string, "citations": [{ "path": string, "start_line": number, "end_line": number, "why": string }], "confidence": number (0..1) }.
The path + line range in each citation MUST be copied verbatim from a provided snippet.`;

// ─────────────────────────────────────────────────────────────────────────────
// SERVER PROMPT for "Snap code" (operator must mirror at /api/apps/oriora_knowcode/extract-code).
// The request body is { image: <data:image/... JPEG data-URL> }. The server runs ONE image-capable
// call and returns { text: string } — the code/error/diagram text read out of the photo, verbatim,
// with NO fences, commentary or guessing. "" when nothing is readable. The app then searches its
// own on-device index with that text; the model never sees the repo.
// ─────────────────────────────────────────────────────────────────────────────
export const EXTRACT_SYSTEM_PROMPT = `You transcribe the text shown in a single photograph for Oriora KnowCode. The photo is of a code snippet, an error/stack-trace message, or an architecture diagram — from a screen, a book or a slide.

ABSOLUTE RULES:
1. TRANSCRIBE ONLY WHAT IS VISIBLE. Read the code, error text or diagram labels exactly as shown. Preserve identifiers, function names, error messages and symbols verbatim. Never invent, complete or correct code that is not clearly in the image.
2. NO WRAPPING. Return the raw text only — no Markdown code fences, no language tag, no commentary, no explanation, no restating the task.
3. DIAGRAMS. For an architecture/flow diagram, list the box/node labels and the connections between them as plain lines.
4. UNREADABLE. If the image contains no readable code or text, return an empty string.

Return JSON: { "text": string }.`;
